import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Stack, Pivot, PivotItem, getTheme, Text, PrimaryButton, TextField, Modal } from '@fluentui/react';
import { useDisplayCreateWidgetModal, useSelectedArtwork, useWidgetsList, useCurrentWidgetIndex } from '../../states/hooks';
import { more, globe, photo, quote, upload, book } from '../../assets/icons';
import { widgetStyles } from './Widget.styles';
import ArtGallery from './ArtGallery/ArtGallery';
import UploadImage from './ImageUpload/ImageUpload';
import Quote from './Quote/Quote';
import Book from './Book/Book';
import URL from './URL/URL';
import { useCreateOrUpdateWidget, useEditedWidget } from '../../states/hooks';
import { trackUnsplashDownload } from './ImageUpload/unsplash';
import IvicosStrings from 'kits/language/stringKit';

const theme = getTheme();

const CustomPivotHeader = ({ icon, text }: { icon: string; text: string }) => (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
        <img src={icon} alt="" style={{ width: 16, height: 16 }} />
        <Text>{text}</Text>
    </Stack>
);

const CreateWidget: React.FC = () => {
    const [header, setHeader] = useState('');
    const [footer, setFooter] = useState('');
    const [selectedPivot, setSelectedPivot] = useState('art-gallery');
    const [displayCreateWidgetModal, setDisplayCreateWidgetModal] = useDisplayCreateWidgetModal();
    const [isSaving, setIsSaving] = useState(false);
    const [contentData, setContentData] = useState<any>({});
    const [createOrUpdateWidget] = useCreateOrUpdateWidget();
    const [editedWidget, setEditedWidget] = useEditedWidget();
    const [widgetsList, setWidgetsList] = useWidgetsList();
    const [, setSelectedArtwork] = useSelectedArtwork();
    const [, setCurrentWidgetIndex] = useCurrentWidgetIndex();

    useEffect(() => {
        if (displayCreateWidgetModal) setSelectedPivot(displayCreateWidgetModal);
    }, [displayCreateWidgetModal]);

    useEffect(() => {
        if (editedWidget) {
            setHeader(editedWidget.header || '');
            setFooter(editedWidget.footer || '');
            setContentData(editedWidget.content || {});
        }
    }, [editedWidget]);

    const handleCreateWidget = async () => {
        setIsSaving(true);

        // Track Unsplash download if the widget contains an Unsplash image
        if (contentData?.imageUrl && contentData.imageUrl.includes('unsplash') && contentData.downloadLocation) {
            await trackUnsplashDownload(contentData.downloadLocation);
        }

        const newWidget = {
            id: crypto.randomUUID(),
            type: selectedPivot,
            header,
            footer,
            content: contentData,
            createdAt: new Date().toISOString()
        };

        const newWidgetsList = [...widgetsList, newWidget];
        setWidgetsList(newWidgetsList);
        const maxVisibleWidgets = 2;
        const newWidgetIndex = newWidgetsList.length - 1;
        const targetIndex = Math.max(0, newWidgetIndex - maxVisibleWidgets + 1);
        setCurrentWidgetIndex(targetIndex);

        onDismiss();
        setIsSaving(false);
    };

    const handleUpdateWidget = () => {
        if (!editedWidget?.id) return;

        setIsSaving(true);

        const widgetToUpdate = widgetsList.find((widget) => widget.id === editedWidget?.id);
        if (!widgetToUpdate) return;

        const updatedWidget = {
            ...widgetToUpdate,
            header,
            footer,
            content: contentData,
            updatedAt: new Date().toISOString()
        };
        setWidgetsList(widgetsList.map((widget) => (widget.id === editedWidget?.id ? updatedWidget : widget)));
        onDismiss();
        setIsSaving(false);
    };

    const onDismiss = () => {
        setDisplayCreateWidgetModal('');
        setSelectedArtwork(null);
        setHeader('');
        setFooter('');
        setContentData({});
        setEditedWidget(null);
    };

    const handleContentSelect = useCallback((content: any) => {
        setContentData(content);
    }, []);

    const isWidgetValid = () => {
        // Check if contentData exists and has properties
        if (!contentData || Object.keys(contentData).length === 0) {
            return false;
        }

        // Widget-specific validation
        if (selectedPivot === 'quote') {
            const trimmedQuote = contentData?.quote?.trim();
            const trimmedAuthor = contentData?.author?.trim();
            return trimmedQuote && trimmedAuthor;
        }

        if (selectedPivot === 'image-upload' || selectedPivot === 'art-gallery') {
            return contentData?.imageUrl;
        }

        if (selectedPivot === 'book') {
            return contentData?.title && contentData?.author;
        }

        if (selectedPivot === 'urls') {
            return contentData?.links && contentData.links.length > 0;
        }

        return true; // Other widget types don't have specific validation
    };

    return (
        <Modal isOpen={displayCreateWidgetModal !== ''} onDismiss={onDismiss} isBlocking={false} styles={{ main: widgetStyles.modalStyles }}>
            <Stack horizontal horizontalAlign="space-between">
                <Text variant="large" style={{ fontWeight: 'bold', marginBottom: 15 }}>
                    {createOrUpdateWidget === 'create'
                        ? IvicosStrings.personalMeetingCornerStrings.createWidget.createWidget
                        : IvicosStrings.personalMeetingCornerStrings.createWidget.updateWidget}
                </Text>
                <IconButton
                    iconProps={{ iconName: 'Cancel' }}
                    ariaLabel={IvicosStrings.personalMeetingCornerStrings.createWidget.close}
                    onClick={onDismiss}
                    styles={{ root: { width: 30, height: 30, color: theme.palette.neutralSecondary } }}
                />
            </Stack>

            <Pivot
                selectedKey={selectedPivot}
                onLinkClick={(item) => {
                    // Disable pivot switching when editing
                    if (createOrUpdateWidget === 'update') return;
                    setSelectedPivot(item?.props.itemKey || selectedPivot);
                }}
                linkFormat="links"
                linkSize="normal"
                styles={{
                    link: createOrUpdateWidget === 'create' ? widgetStyles.pivotLinkClass : widgetStyles.pivotLinkDisabledClass,
                    linkIsSelected: widgetStyles.pivotLinkClass,
                    root: widgetStyles.pivotRootClass
                }}
            >
                <PivotItem
                    itemKey="art-gallery"
                    onRenderItemLink={() => <CustomPivotHeader icon={photo} text={IvicosStrings.personalMeetingCornerStrings.createWidget.artGallery} />}
                >
                    <ArtGallery onImageSelect={handleContentSelect} />
                </PivotItem>
                <PivotItem
                    itemKey="image-upload"
                    onRenderItemLink={() => <CustomPivotHeader icon={upload} text={IvicosStrings.personalMeetingCornerStrings.createWidget.imageUpload} />}
                >
                    <UploadImage onImageSelect={handleContentSelect} />
                </PivotItem>
                <PivotItem
                    itemKey="quote"
                    onRenderItemLink={() => <CustomPivotHeader icon={quote} text={IvicosStrings.personalMeetingCornerStrings.createWidget.quote} />}
                >
                    <Quote onQuoteSelect={handleContentSelect} />
                </PivotItem>
                <PivotItem
                    itemKey="urls"
                    onRenderItemLink={() => <CustomPivotHeader icon={globe} text={IvicosStrings.personalMeetingCornerStrings.createWidget.urls} />}
                >
                    <URL onURLSelect={handleContentSelect} />
                </PivotItem>
                <PivotItem
                    itemKey="book"
                    onRenderItemLink={() => <CustomPivotHeader icon={book} text={IvicosStrings.personalMeetingCornerStrings.createWidget.book} />}
                >
                    <Book onBookSelect={handleContentSelect} />
                </PivotItem>

                {/* <PivotItem itemKey="another-life" onRenderItemLink={() => <CustomPivotHeader icon={more} text="Another Life" />}>
                    <p>Another Life section (to be implemented)</p>
                </PivotItem> */}
            </Pivot>

            <TextField
                label={IvicosStrings.personalMeetingCornerStrings.createWidget.header}
                placeholder={IvicosStrings.personalMeetingCornerStrings.createWidget.widgetHeader}
                value={header}
                onChange={(_, newValue) => setHeader(newValue || '')}
            />
            <TextField
                label={IvicosStrings.personalMeetingCornerStrings.createWidget.footer}
                placeholder={IvicosStrings.personalMeetingCornerStrings.createWidget.widgetFooter}
                value={footer}
                onChange={(_, newValue) => setFooter(newValue || '')}
            />
            <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="end" style={{ marginTop: 20 }}>
                <PrimaryButton
                    text={
                        createOrUpdateWidget === 'create'
                            ? IvicosStrings.personalMeetingCornerStrings.createWidget.createWidgetButton
                            : IvicosStrings.personalMeetingCornerStrings.createWidget.updateWidgetButton
                    }
                    iconProps={{ iconName: 'Add' }}
                    onClick={createOrUpdateWidget === 'create' ? handleCreateWidget : handleUpdateWidget}
                    disabled={isSaving || !isWidgetValid()}
                />
            </Stack>
        </Modal>
    );
};

export default CreateWidget;
